import { Button, Typography } from "@hellocarbo/lasagne";
import { use, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { UserContext } from "@/contexts/UserContext";
import useOutboundQueryParams from "@/hooks/useOutboundQueryParams";
import useSegmentEvent from "@/hooks/useSegmentEvent";

import Card from "../ui/Card";
import FeatureList from "./FeatureList";

type SubscriptionCard = {
  subscriptionType: "pro" | "essential" | "premium";
  price: number;
  className?: string;
};

const SubscriptionCard = ({ subscriptionType, price }: SubscriptionCard) => {
  const { user } = use(UserContext);
  const params = useOutboundQueryParams();
  const proDemoUrl = `https://www.hellocarbo.com/parler-de-votre-entreprise-publique${params}`;
  const defaultDemoUrl = `https://www.hellocarbo.com/reservez-votre-demo${params}`;

  const { t, i18n, ready } = useTranslation(["pricing", "formatter"]);
  const { sendSegmentEvent } = useSegmentEvent();

  const [features, setFeatures] = useState<string[]>([]);

  useEffect(() => {
    if (ready) {
      setFeatures(
        t(`pricing:${subscriptionType}:features`, {
          returnObjects: true,
        }) as string[]
      );
    } else {
      setFeatures(["...", "...", "...", "...", "..."]);
    }
  }, [subscriptionType, ready, i18n.language]);

  return (
    <Card className="grid-template-columns-[1fr,_auto] gap-x-3xl gap-y-md grid lg:flex lg:flex-col lg:gap-0">
      <div className="col-1">
        <Typography step="2" autoSpacing>
          {t(`pricing:${subscriptionType}:title`)}
        </Typography>
        <Typography step="0" autoSpacing>
          {t(`pricing:${subscriptionType}:description`)}
        </Typography>
      </div>
      <div className="my-lg col-start-2 row-span-2 flex flex-col items-center self-center">
        <Typography step="0" className="self-start">
          {t("pricing:from")}
        </Typography>
        <Typography step="5" className="mb-md self-start">
          {t("formatter:currency", { value: price, maximumFractionDigits: 0 })}
        </Typography>
        <Button
          intent="secondary"
          as="a"
          href={subscriptionType === "pro" ? proDemoUrl : defaultDemoUrl}
          shouldOpenNewTab
          className="mb-2xs"
          onClick={() => {
            sendSegmentEvent("Subscription Asked", {
              size: `${user?.company.employees}`,
            });
          }}
        >
          {t(`pricing:cta`)}
        </Button>
        <Typography step="0" className="text-grayscale">
          {t(`pricing:duration`)}
        </Typography>
      </div>
      <div>
        {i18n.exists(`pricing:${subscriptionType}:pricingInfo`) ? (
          <Typography step="0" weight="bold" autoSpacing>
            {t(`pricing:${subscriptionType}:pricingInfo`)}
          </Typography>
        ) : null}
        <FeatureList items={features} />
      </div>
    </Card>
  );
};

export default SubscriptionCard;
